import { useMemo, useState } from "react";
import {
  Activity,
  AlertTriangle,
  Link2,
  Search,
  ShieldCheck,
  Users,
} from "lucide-react";
import { Card, CardContent } from "../../../client/components/ui/card";
import { Badge } from "../../../client/components/ui/badge";
import { Input } from "../../../client/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../client/components/ui/select";
import useTranslation from "../../../client/hooks/useTranslation";
import { cn } from "../../../client/utils";
import type {
  AdminPlanColumn,
  AdminPlanUser,
} from "../../../analytics/adminUsersByPlan";
import { INACTIVE_AFTER_DAYS } from "../../../analytics/adminPlanGrouping";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

type SortKey = "newest" | "oldest" | "lastActive" | "email";

interface Props {
  columns: AdminPlanColumn[];
  /** Actifs sur 7 jours, tous plans confondus. */
  activeLast7Days?: number;
  isLoading?: boolean;
  onSelectUser?: (userId: string) => void;
}

const COLUMN_STYLES: Record<string, { label: string; accent: string }> = {
  max: { label: "Max", accent: "text-primary" },
  pro: { label: "Pro", accent: "text-secondary" },
  free: { label: "Free", accent: "text-muted-foreground" },
};

function isInactive(lastActiveAt: Date | null): boolean {
  if (!lastActiveAt) return true;
  return Date.now() - new Date(lastActiveAt).getTime() > INACTIVE_AFTER_DAYS * MS_PER_DAY;
}

function formatDate(value: Date | null): string | null {
  if (!value) return null;
  return new Date(value).toLocaleDateString();
}

const UserRow = ({
  user,
  onSelect,
}: {
  user: AdminPlanUser;
  onSelect?: (userId: string) => void;
}) => {
  const t = useTranslation();
  const inactive = isInactive(user.lastActiveAt);
  const lastActive = formatDate(user.lastActiveAt);

  return (
    <div
      role={onSelect ? "button" : undefined}
      tabIndex={onSelect ? 0 : undefined}
      onClick={() => onSelect?.(user.id)}
      onKeyDown={(event) => {
        if (!onSelect) return;
        // Espace et Entree : la ligne est un bouton, elle doit se comporter
        // comme tel au clavier.
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect(user.id);
        }
      }}
      className={cn(
        "border-border/60 flex flex-col gap-1.5 border-b py-3 last:border-b-0",
        onSelect &&
          "hover:bg-muted/50 focus-visible:ring-ring -mx-2 cursor-pointer rounded px-2 focus-visible:outline-none focus-visible:ring-1",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-foreground truncate text-sm font-medium">
            {user.email ?? user.username ?? user.id}
          </p>
          {user.username && user.email && (
            <p className="text-muted-foreground truncate text-xs">
              {user.username}
            </p>
          )}
        </div>
        {inactive && (
          <span
            className="bg-muted-foreground/50 mt-1.5 size-2 shrink-0 rounded-full"
            title={t.adminDashboard.inactiveBadge}
          />
        )}
      </div>

      <div className="flex flex-wrap items-center gap-1">
        {user.isAnnual && (
          <Badge variant="outline" className="text-[10px]">
            {t.adminDashboard.annualBadge}
          </Badge>
        )}
        {user.isAdmin && (
          <Badge variant="outline" className="gap-1 text-[10px]">
            <ShieldCheck className="size-3" />
            {t.adminDashboard.adminBadge}
          </Badge>
        )}
        {user.hasBrokerConnection && (
          <Badge variant="outline" className="gap-1 text-[10px]">
            <Link2 className="size-3" />
            {t.adminDashboard.brokerBadge}
          </Badge>
        )}
        {user.hasPaymentIssue && (
          <Badge variant="destructive" className="gap-1 text-[10px]">
            <AlertTriangle className="size-3" />
            {user.graceDaysLeft > 0
              ? `${t.adminDashboard.graceBadge} (${user.graceDaysLeft}j)`
              : t.adminDashboard.expiredBadge}
          </Badge>
        )}
      </div>

      <p className="text-muted-foreground text-[11px]">
        {t.adminDashboard.signedUpOn} {formatDate(user.createdAt)}
        {" · "}
        {lastActive
          ? `${t.adminDashboard.lastActiveOn} ${lastActive}`
          : t.adminDashboard.neverActive}
      </p>
    </div>
  );
};

const UsersByPlanColumns = ({
  columns,
  activeLast7Days,
  isLoading,
  onSelectUser,
}: Props) => {
  const t = useTranslation();
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortKey>("newest");

  const filteredColumns = useMemo(() => {
    const needle = search.trim().toLowerCase();

    return columns.map((column) => {
      const users = column.users.filter((user) => {
        if (!needle) return true;
        return (
          (user.email ?? "").toLowerCase().includes(needle) ||
          (user.username ?? "").toLowerCase().includes(needle)
        );
      });

      const sorted = [...users].sort((a, b) => {
        switch (sort) {
          case "oldest":
            return (
              new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
            );
          case "lastActive": {
            // Les jamais-connectes vont en fin de liste plutot que d'etre
            // traites comme "vus en 1970", qui les remonterait en tete.
            const aTime = a.lastActiveAt
              ? new Date(a.lastActiveAt).getTime()
              : -Infinity;
            const bTime = b.lastActiveAt
              ? new Date(b.lastActiveAt).getTime()
              : -Infinity;
            return bTime - aTime;
          }
          case "email":
            return (a.email ?? "").localeCompare(b.email ?? "");
          case "newest":
          default:
            return (
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            );
        }
      });

      return { ...column, users: sorted };
    });
  }, [columns, search, sort]);

  return (
    <div>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-foreground text-lg font-semibold">
            {t.adminDashboard.usersByPlanTitle}
          </h2>
          <p className="text-muted-foreground text-xs">
            {t.adminDashboard.usersByPlanSubtitle}
          </p>
          {activeLast7Days !== undefined && (
            <p className="text-muted-foreground mt-1 flex items-center gap-1.5 text-xs">
              <Activity className="text-primary size-3.5" />
              <span className="text-foreground font-mono font-semibold">
                {activeLast7Days}
              </span>
              {t.adminDashboard.activeUsersTotal}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative">
            <Search className="text-muted-foreground pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t.adminDashboard.searchPlaceholder}
              className="pl-8 sm:w-64"
            />
          </div>
          <Select
            value={sort}
            onValueChange={(value) => setSort(value as SortKey)}
          >
            <SelectTrigger className="sm:w-48">
              <SelectValue placeholder={t.adminDashboard.sortBy} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">
                {t.adminDashboard.sortNewest}
              </SelectItem>
              <SelectItem value="oldest">
                {t.adminDashboard.sortOldest}
              </SelectItem>
              <SelectItem value="lastActive">
                {t.adminDashboard.sortLastActive}
              </SelectItem>
              <SelectItem value="email">{t.adminDashboard.sortEmail}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {filteredColumns.map((column) => {
          const style = COLUMN_STYLES[column.key] ?? {
            label: column.key,
            accent: "text-foreground",
          };

          return (
            <Card key={column.key} className={cn(isLoading && "opacity-50")}>
              <CardContent className="pt-4">
                <div className="border-border mb-2 border-b pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Users className={cn("size-4", style.accent)} />
                      <span className={cn("font-semibold", style.accent)}>
                        {style.label}
                      </span>
                    </div>
                    {/* Compteur du sous-ensemble affiche : pendant une
                        recherche, montrer le total de la colonne contredirait
                        la liste. */}
                    <span className="text-foreground font-mono text-lg font-bold">
                      {column.users.length}
                    </span>
                  </div>

                  {/* Les actifs viennent du serveur et portent sur la colonne
                      ENTIERE : ils ne suivent pas la recherche, qui ne filtre
                      que la liste affichee. */}
                  <div
                    className="text-muted-foreground mt-1.5 flex items-center gap-1.5 text-xs"
                    title={t.adminDashboard.activeUsersHint}
                  >
                    <Activity className="size-3.5" />
                    <span className="text-foreground font-mono font-semibold">
                      {column.activeLast7Days}
                    </span>
                    <span>{t.adminDashboard.activeUsersLabel}</span>
                    {column.count > 0 && (
                      <span className="ml-auto font-mono">
                        {Math.round(
                          (column.activeLast7Days / column.count) * 100,
                        )}
                        %
                      </span>
                    )}
                  </div>
                </div>

                <div className="max-h-[32rem] overflow-y-auto pr-1">
                  {column.users.length === 0 ? (
                    <p className="text-muted-foreground py-6 text-center text-xs">
                      {search.trim()
                        ? t.adminDashboard.noSearchResult
                        : t.adminDashboard.emptyColumn}
                    </p>
                  ) : (
                    column.users.map((user) => (
                      <UserRow
                        key={user.id}
                        user={user}
                        onSelect={onSelectUser}
                      />
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default UsersByPlanColumns;
